import React, { useState, useEffect, useCallback, useRef, memo } from "react";
import { addDays, differenceInDays, isSameDay, isBefore } from "date-fns";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styles from "./styles.module.scss";

// Components
import FormRow from "./components/FormRow";
import TextInput from "./components/TextInput";
import FormButtons from "./components/FormButtons";
import FormSection from "./components/FormSection";
import SelectInput from "./components/SelectInput";
import ErrorMessage from "./components/ErrorMessage";
import SuccessState from "./components/SuccessState";
import { DispositionTypes } from "modals/BookingForm/components";

// Services & Utilities
import StaffService from "services/staffService";
import { createPlaceGroupService } from "services/placeGroupService";
import getFilteredDispositionTypes from "helpers/getSpaceDispositionTypes";
import { fetchSpaceData } from "services/spacePage.services";
import { fetchPlaceDates } from "services/booking.services";

// Helper functions
import {
  formatDateToString,
  formatDateForDisplay,
  createStableLocalDate,
  isSameDate,
  timeToHour,
  hourToTime,
} from "reusableComponents/booking/utils/dateTimeHelpers";

// Memoized component for selected date display
const SelectedDateDisplay = memo(({ selectedDate, selectedTime, currentLanguage }) => {
  const display = formatDateForDisplay(selectedDate, currentLanguage);
  
  if (!display.dayOfMonth) return null;
  
  return (
    <span className={styles.selectedDate}>
      <span className={styles.dateDisplay}>
        {display.dayOfMonth} {display.monthYear}
        {selectedTime && ` • ${selectedTime}`}
      </span>
    </span>
  );
});

SelectedDateDisplay.displayName = 'SelectedDateDisplay';

// Generate time slots with 30-minute intervals
const generateTimeSlots = (openingTime, closingTime) => {
  const slots = [];
  const startHour = timeToHour(openingTime);
  const endHour = timeToHour(closingTime);
  
  let current = openingTime;
  
  while (timeToHour(current) < endHour) {
    slots.push(current);
    const [hours, minutes] = current.split(":").map(Number);
    const nextMinutes = minutes + 30;
    const nextHour = hours + Math.floor(nextMinutes / 60);
    const nextMinute = nextMinutes % 60;
    current = `${nextHour.toString().padStart(2, "0")}:${nextMinute.toString().padStart(2, "0")}`;
  }
  
  return slots;
};

// Check if two time intervals overlap (EXACT same as CompactBookingForm)
const isTimeOverlapping = (start, end, bookedSlotsList) => {
  const startHour = timeToHour(start);
  const endHour = timeToHour(end);
  return bookedSlotsList.some(booked => {
    const bookedStart = timeToHour(booked.from);
    const bookedEnd = timeToHour(booked.to);
    return (startHour < bookedEnd && endHour > bookedStart);
  });
};

const BookingModal = ({
  initialBooking = null,
  onClose = () => {},
  onSubmit,
  onDelete = () => {},
}) => {
  const { t, i18n } = useTranslation("global");
  const { language } = i18n;
  
  // Data states
  const [groups, setGroups] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [staff, setStaff] = useState([]);
  
  // Loading states for dropdowns
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingSpaces, setLoadingSpaces] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);
  
  // Booking form state
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [availableStartTimes, setAvailableStartTimes] = useState([]);
  const [availableEndTimes, setAvailableEndTimes] = useState([]);
  const [capacity, setCapacity] = useState(1);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [activeDatePicker, setActiveDatePicker] = useState(null);
  const [selectedDateTemp, setSelectedDateTemp] = useState(null);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Ref to track if spaces are already loaded
  const spacesLoadedRef = useRef(false);
  
  // Ref to track previous place value
  const prevPlaceRef = useRef(null);
  
  // Store original booking times for each space
  const originalTimesRef = useRef({});
  
  // Form data for submission - initialize with "No group" selected
  const [formData, setFormData] = useState({
    title: "",
    groupFilter: { id: '%7Bid%7D', label: "No group", value: '%7Bid%7D' },
    place: null,
    staff: null,
    customer_social_reason: "",
    customer_first_name: "",
    customer_last_name: "",
    customer_email: "",
    customer_phone: "",
    services_equipments: "",
    capacity: 1,
    disposition_type: null,
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [sendMail, setSendMail] = useState(false);
  const [newbookingSuccess, setNewbookingSuccess] = useState(false);
  
  // Full space details
  const [selectedSpaceDetails, setSelectedSpaceDetails] = useState(null);
  
  const token = useSelector((state) => state.auth.token);
  const PlaceGroupService = React.useMemo(() => createPlaceGroupService(token), [token]);
  
  const calendarRef = useRef(null);
  const startDateButtonRef = useRef(null);
  const endDateButtonRef = useRef(null);
  const modalContainerRef = useRef(null);
  const isMountedRef = useRef(true);
  
  const isCoworking = selectedSpaceDetails?.placeUsage?.key === "coworking";
  const maxCapacity = isCoworking ? selectedSpaceDetails?.coworking_stations : selectedSpaceDetails?.max_capacity;
  
  const minDurationInHours = selectedSpaceDetails?.minimal_duration === "half_day" ? 4 : 
                            selectedSpaceDetails?.minimal_duration === "day" ? 8 : 1;
  
  const currentLanguage = language || "en";
  
  // Weekday names for calendar
  const weekdays = [
    t("days.short.monday"),
    t("days.short.tuesday"),
    t("days.short.wednesday"),
    t("days.short.thursday"),
    t("days.short.friday"),
    t("days.short.saturday"),
    t("days.short.sunday")
  ];
  
  // Parse day string to get working intervals
  const getWorkingIntervalsForDate = useCallback((date) => {
    if (!selectedSpaceDetails || !date) return [];
    
    const stableDate = createStableLocalDate(date);
    if (!stableDate) return [];
    
    const jsDayOfWeek = stableDate.getDay();
    const dayIndex = jsDayOfWeek;
    const dayString = selectedSpaceDetails[`day_${dayIndex}`];
    
    if (!dayString) return [];
    
    const intervals = [];
    let inInterval = false;
    let intervalStart = null;
    
    for (let i = 0; i < dayString.length; i++) {
      const isOpen = dayString[i] === '1';
      const hour = Math.floor(i / 2);
      const minute = (i % 2) * 30;
      const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      
      if (isOpen && !inInterval) {
        inInterval = true;
        intervalStart = timeStr;
      } else if (!isOpen && inInterval) {
        inInterval = false;
        intervals.push({ from: intervalStart, to: timeStr });
      }
    }
    
    if (inInterval) {
      intervals.push({ from: intervalStart, to: "24:00" });
    }
    
    return intervals;
  }, [selectedSpaceDetails]);
  
  const isWorkingDay = useCallback((date) => {
    const intervals = getWorkingIntervalsForDate(date);
    return intervals.length > 0;
  }, [getWorkingIntervalsForDate]);
  
  // Fetch bookings for a date - includes reservations, private_reservations, and calendar_events
  const fetchBookingsForDate = useCallback(async (date, excludeCurrentBooking = false) => {
    if (!date || !selectedSpaceDetails?.id) return [];
    
    const formattedDate = formatDateToString(date);
    setLoadingAvailability(true);
    
    try {
      const response = await fetchPlaceDates(selectedSpaceDetails.id, formattedDate, formattedDate, token);
      
      const booked = [];
      
      if (response?.success && response?.data) {
        // Process private reservations
        if (response.data.private_reservations && Array.isArray(response.data.private_reservations)) {
          response.data.private_reservations.forEach(res => {
            if (res.from && res.to && res.start_date && res.end_date) {
              if (formattedDate >= res.start_date && formattedDate <= res.end_date) {
                let shouldExclude = false;
                
                if (excludeCurrentBooking && initialBooking) {
                  // Match by date
                  const matchesDate = res.start_date === initialBooking.start_date && 
                                     res.end_date === initialBooking.end_date;
                  
                  // Match by time - compare without seconds
                  const resFrom = res.from.slice(0, 5);
                  const resTo = res.to.slice(0, 5);
                  const matchesTime = resFrom === initialBooking.start_hour && 
                                     resTo === initialBooking.end_hour;
                  
                  // Also try to match by ID if available
                  const matchesId = res.id && res.id === initialBooking.id;
                  
                  shouldExclude = (matchesDate && matchesTime) || matchesId;
                  
                  if (shouldExclude) {
                    return;
                  }
                }
                booked.push({ 
                  from: res.from.slice(0, 5), 
                  to: res.to.slice(0, 5),
                  id: res.id,
                  type: 'private_reservation'
                });
              }
            }
          });
        }
        
        // Process regular reservations
        if (response.data.reservations && Array.isArray(response.data.reservations)) {
          response.data.reservations.forEach(res => {
            if (res.date === formattedDate && res.from && res.to) {
              let shouldExclude = false;
              if (excludeCurrentBooking && initialBooking) {
                const matchesDate = res.date === initialBooking.start_date;
                const resFrom = res.from.slice(0, 5);
                const resTo = res.to.slice(0, 5);
                const matchesTime = resFrom === initialBooking.start_hour && 
                                   resTo === initialBooking.end_hour;
                const matchesId = res.id && res.id === initialBooking.id;
                shouldExclude = (matchesDate && matchesTime) || matchesId;
              }
              if (shouldExclude) {
                return;
              }
              booked.push({ 
                from: res.from.slice(0, 5), 
                to: res.to.slice(0, 5),
                id: res.id,
                type: 'reservation'
              });
            }
          });
        }
        
        // Process calendar_events
        if (response.data.calendar_events && Array.isArray(response.data.calendar_events)) {
          response.data.calendar_events.forEach(event => {
            if (event.start_date && event.end_date) {
              if (formattedDate >= event.start_date && formattedDate <= event.end_date) {
                let shouldExclude = false;
                if (excludeCurrentBooking && initialBooking) {
                  const matchesId = event.id && event.id === initialBooking.id;
                  shouldExclude = matchesId;
                }
                if (shouldExclude) {
                  return;
                }
                let fromTime = event.start_hour || "00:00";
                let toTime = event.end_hour || "23:59";
                
                if (event.start_date.includes('T')) {
                  const startDateTime = new Date(event.start_date);
                  fromTime = startDateTime.toTimeString().slice(0, 5);
                  const endDateTime = new Date(event.end_date);
                  toTime = endDateTime.toTimeString().slice(0, 5);
                }
                
                booked.push({ 
                  from: fromTime.slice(0, 5), 
                  to: toTime.slice(0, 5),
                  id: event.id,
                  type: 'calendar_event'
                });
              }
            }
          });
        }
      }
      
      return booked;
    } catch (err) {
      console.error('Error fetching bookings:', err);
    } finally {
      setLoadingAvailability(false);
    }
    return [];
  }, [selectedSpaceDetails?.id, token, initialBooking]);
  
  // Load availability when date is selected or space changes
  useEffect(() => {
    if (!selectedDateTemp) {
      setBookedSlots([]);
      return;
    }
    
    const isEditing = !!initialBooking?.id;
    const loadAvailability = async () => {
      const booked = await fetchBookingsForDate(selectedDateTemp, isEditing);
      setBookedSlots(booked);
      
      if (activeDatePicker === 'start') {
        const times = getAvailableStartTimesForDate(selectedDateTemp);
        setAvailableStartTimes(times);
      }
      
      if (activeDatePicker === 'end' && startTime) {
        const isSameDayBooking = startDate && isSameDay(selectedDateTemp, startDate);
        const times = getAvailableEndTimesForDate(selectedDateTemp, isSameDayBooking, startTime);
        setAvailableEndTimes(times);
      }
    };
    
    loadAvailability();
  }, [selectedDateTemp, activeDatePicker, fetchBookingsForDate, initialBooking, selectedSpaceDetails?.id]);
  
  // Get available start times
  const getAvailableStartTimesForDate = useCallback((date) => {
    if (!date) return [];
    
    const intervals = getWorkingIntervalsForDate(date);
    if (intervals.length === 0) return [];
    
    const availableStarts = [];
    const isEditing = !!initialBooking?.id;
    
    intervals.forEach(interval => {
      const allStartTimes = generateTimeSlots(interval.from, interval.to);
      
      allStartTimes.forEach(startTime => {
        const startHour = timeToHour(startTime);
        const intervalEndHour = timeToHour(interval.to);
        
        const endHour = startHour + minDurationInHours;
        const endTimeStr = hourToTime(endHour);
        
        if (endHour > intervalEndHour) {
          return;
        }
        
        const hasConflict = bookedSlots.some(booked => {
          if (isEditing && booked.id === initialBooking?.id) {
            return false;
          }
          return isTimeOverlapping(startTime, endTimeStr, [booked]);
        });
        
        if (!hasConflict && !availableStarts.includes(startTime)) {
          availableStarts.push(startTime);
        }
      });
    });
    
    return availableStarts.sort();
  }, [bookedSlots, getWorkingIntervalsForDate, minDurationInHours, initialBooking]);
  
  // Get available end times
  const getAvailableEndTimesForDate = useCallback((date, isSameDayBooking, startTimeParam) => {
    if (!date) return [];
    
    const intervals = getWorkingIntervalsForDate(date);
    if (intervals.length === 0) return [];
    
    const availableEnds = [];
    const isEditing = !!initialBooking?.id;
    
    if (isSameDayBooking && startTimeParam) {
      const startHour = timeToHour(startTimeParam);
      
      let startInterval = null;
      for (const interval of intervals) {
        const intervalStart = timeToHour(interval.from);
        const intervalEnd = timeToHour(interval.to);
        if (startHour >= intervalStart && startHour < intervalEnd) {
          startInterval = interval;
          break;
        }
      }
      
      if (!startInterval) return [];
      
      const intervalEndHour = timeToHour(startInterval.to);
      const minEndHour = startHour + minDurationInHours;
      const maxEndHour = intervalEndHour;
      
      for (let hour = minEndHour; hour <= maxEndHour + 0.1; hour += 0.5) {
        const roundedHour = Math.round(hour * 2) / 2;
        if (roundedHour > maxEndHour) break;
        
        const endTimeStr = hourToTime(roundedHour);
        
        const hasConflict = bookedSlots.some(booked => {
          if (isEditing && booked.id === initialBooking?.id) {
            return false;
          }
          return isTimeOverlapping(startTimeParam, endTimeStr, [booked]);
        });
        
        if (!hasConflict && !availableEnds.includes(endTimeStr)) {
          availableEnds.push(endTimeStr);
        }
      }
    } 
    else if (!isSameDayBooking) {
      const firstInterval = intervals[0];
      const lastInterval = intervals[intervals.length - 1];
      const openingHour = timeToHour(firstInterval.from);
      const closingHour = timeToHour(lastInterval.to);
      
      const minEndHour = openingHour + minDurationInHours;
      const maxEndHour = closingHour;
      
      for (let hour = minEndHour; hour <= maxEndHour + 0.1; hour += 0.5) {
        const roundedHour = Math.round(hour * 2) / 2;
        if (roundedHour > maxEndHour) break;
        
        const endTimeStr = hourToTime(roundedHour);
        
        let isInInterval = false;
        for (const interval of intervals) {
          const intervalStart = timeToHour(interval.from);
          const intervalEnd = timeToHour(interval.to);
          if (roundedHour > intervalStart && roundedHour <= intervalEnd) {
            isInInterval = true;
            break;
          }
        }
        
        if (!isInInterval) continue;
        
        const hasConflict = bookedSlots.some(booked => {
          if (isEditing && booked.id === initialBooking?.id) {
            return false;
          }
          const openingTimeStr = firstInterval.from;
          return isTimeOverlapping(openingTimeStr, endTimeStr, [booked]);
        });
        
        if (!hasConflict && !availableEnds.includes(endTimeStr)) {
          availableEnds.push(endTimeStr);
        }
      }
    }
    
    return availableEnds.sort();
  }, [bookedSlots, getWorkingIntervalsForDate, minDurationInHours, initialBooking]);
  
  // Update available start times when bookedSlots changes
  useEffect(() => {
    if (activeDatePicker === 'start' && selectedDateTemp) {
      const times = getAvailableStartTimesForDate(selectedDateTemp);
      setAvailableStartTimes(times);
    }
  }, [bookedSlots, activeDatePicker, selectedDateTemp, getAvailableStartTimesForDate]);
  
  // Update available end times when bookedSlots changes
  useEffect(() => {
    if (activeDatePicker === 'end' && selectedDateTemp && startTime) {
      const isSameDayBooking = startDate && isSameDay(selectedDateTemp, startDate);
      const times = getAvailableEndTimesForDate(selectedDateTemp, isSameDayBooking, startTime);
      setAvailableEndTimes(times);
    }
  }, [bookedSlots, activeDatePicker, selectedDateTemp, startTime, startDate, getAvailableEndTimesForDate]);
  
  // Initialize edit form with existing booking data
  useEffect(() => {
    if (initialBooking && !isInitialized) {
      console.log('[BookingModal] 🚀 Initializing edit form with:', initialBooking);
      
      // Reset spaces loaded flag
      spacesLoadedRef.current = false;
      
      // Store original times for this space
      if (initialBooking.place?.id) {
        originalTimesRef.current[initialBooking.place.id] = {
          start_date: initialBooking.start_date,
          end_date: initialBooking.end_date,
          start_hour: initialBooking.start_hour,
          end_hour: initialBooking.end_hour,
          start_date_dt: initialBooking.start_date_dt,
          end_date_dt: initialBooking.end_date_dt,
          start_hour_dt: initialBooking.start_hour_dt,
          end_hour_dt: initialBooking.end_hour_dt,
        };
        console.log('[BookingModal] 💾 Stored original times for space:', initialBooking.place.id, originalTimesRef.current[initialBooking.place.id]);
      }
      
      // Set form data - use the booking's groupFilter or default to "No group"
      setFormData({
        title: initialBooking.title || "",
        groupFilter: initialBooking.groupFilter || { id: '%7Bid%7D', label: "No group", value: '%7Bid%7D' },
        place: initialBooking.place || null,
        staff: initialBooking.staff || null,
        customer_social_reason: initialBooking.customer_social_reason || "",
        customer_first_name: initialBooking.customer_first_name || "",
        customer_last_name: initialBooking.customer_last_name || "",
        customer_email: initialBooking.customer_email || "",
        customer_phone: initialBooking.customer_phone || "",
        services_equipments: initialBooking.services_equipments || "",
        capacity: initialBooking.capacity || 1,
        disposition_type: initialBooking.disposition_type || null,
      });
      
      // Set dates and times
      if (initialBooking.start_date_dt) {
        const startDateObj = createStableLocalDate(initialBooking.start_date_dt);
        setStartDate(startDateObj);
        setCurrentMonth(startDateObj || new Date());
      } else if (initialBooking.start_date) {
        const startDateObj = createStableLocalDate(initialBooking.start_date);
        setStartDate(startDateObj);
        setCurrentMonth(startDateObj || new Date());
      }
      
      if (initialBooking.end_date_dt) {
        const endDateObj = createStableLocalDate(initialBooking.end_date_dt);
        setEndDate(endDateObj);
      } else if (initialBooking.end_date) {
        const endDateObj = createStableLocalDate(initialBooking.end_date);
        setEndDate(endDateObj);
      }
      
      if (initialBooking.start_hour_dt) {
        setStartTime(initialBooking.start_hour_dt);
      } else if (initialBooking.start_hour) {
        setStartTime(initialBooking.start_hour);
      }
      
      if (initialBooking.end_hour_dt) {
        setEndTime(initialBooking.end_hour_dt);
      } else if (initialBooking.end_hour) {
        setEndTime(initialBooking.end_hour);
      }
      
      // Set capacity
      const initialCapacity = initialBooking.capacity || 1;
      setCapacity(initialCapacity);
      
      // Mark as initialized
      setIsInitialized(true);
      
      // If we have a place, fetch the full space details
      if (initialBooking.place?.id) {
        const fetchSpace = async () => {
          try {
            const fullSpace = await fetchSpaceData(initialBooking.place.id, language);
            setSelectedSpaceDetails(fullSpace);
          } catch (err) {
            console.error("[BookingModal] Error fetching space details:", err);
          }
        };
        fetchSpace();
      }
    }
  }, [initialBooking, language, isInitialized]);
  
  // Update calendar position
  const updateCalendarPosition = useCallback(() => {
    const buttonRef = activeDatePicker === 'start' ? startDateButtonRef : endDateButtonRef;
    if (!buttonRef.current || !modalContainerRef.current) return;
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const modalRect = modalContainerRef.current.getBoundingClientRect();
    const popupWidth = (activeDatePicker === 'start' && selectedDateTemp) || (activeDatePicker === 'end' && selectedDateTemp && startTime) ? 600 : 320;
    
    let left = buttonRect.left - modalRect.left;
    let top = buttonRect.bottom - modalRect.top + 8;
    
    if (left + popupWidth > modalRect.width) {
      left = modalRect.width - popupWidth - 8;
    }
    
    if (left < 0) left = 8;
    
    if (top + 450 > modalRect.height) {
      top = buttonRect.top - modalRect.top - 450 - 8;
    }
    
    setCalendarPosition({ top, left });
  }, [activeDatePicker, selectedDateTemp, startTime]);
  
  // Fetch space details when formData.place changes
  useEffect(() => {
    if (selectedSpaceDetails?.id === formData.place?.id) {
      return;
    }
    
    if (!formData.place?.id) {
      setSelectedSpaceDetails(null);
      return;
    }
    
    const fetchSpace = async () => {
      try {
        setLoadingAvailability(true);
        const fullSpace = await fetchSpaceData(formData.place.id, language);
        setSelectedSpaceDetails(fullSpace);
      } catch (err) {
        console.error("Error fetching full space details:", err);
      } finally {
        setLoadingAvailability(false);
      }
    };
    
    fetchSpace();
  }, [formData.place?.id, language, selectedSpaceDetails]);
  
  // Reset selections when space changes - CLEAN VERSION
  useEffect(() => {
    // Only run if we're initialized
    if (!isInitialized && initialBooking) {
      return;
    }
    
    // Get current place ID
    const currentPlaceId = formData.place?.id;
    
    // If no space selected, just update ref and return
    if (!currentPlaceId) {
      prevPlaceRef.current = null;
      return;
    }
    
    // Check if this is a new space (different from previous)
    const isNewSpace = prevPlaceRef.current !== null && prevPlaceRef.current !== currentPlaceId;
    
    // If it's the same space, nothing to do
    if (!isNewSpace) {
      // Still update the ref if it was null
      if (prevPlaceRef.current === null) {
        prevPlaceRef.current = currentPlaceId;
      }
      return;
    }
    
    // Space has changed - reset!
    console.log('[BookingModal] 🔄 Space changed from', prevPlaceRef.current, 'to', currentPlaceId);
    spacesLoadedRef.current = false;
    
    // Check if we have stored original times for this space
    const storedTimes = originalTimesRef.current[currentPlaceId];
    
    if (storedTimes && initialBooking) {
      // If we have stored times, restore them
      console.log('[BookingModal] 🔄 Restoring stored times for space:', currentPlaceId, storedTimes);
      const startDateObj = createStableLocalDate(storedTimes.start_date_dt || storedTimes.start_date);
      const endDateObj = createStableLocalDate(storedTimes.end_date_dt || storedTimes.end_date);
      setStartDate(startDateObj);
      setEndDate(endDateObj);
      setStartTime(storedTimes.start_hour_dt || storedTimes.start_hour);
      setEndTime(storedTimes.end_hour_dt || storedTimes.end_hour);
      setSelectedDateTemp(startDateObj);
      setActiveDatePicker(null);
    } else {
      // No stored times, reset everything
      console.log('[BookingModal] 🔄 No stored times, resetting everything');
      setStartDate(null);
      setEndDate(null);
      setStartTime(null);
      setEndTime(null);
      setAvailableStartTimes([]);
      setAvailableEndTimes([]);
      setBookedSlots([]);
      setCapacity(1);
      setSelectedDateTemp(null);
      setActiveDatePicker(null);
    }
    
    // Keep the selected space
    setFormData(prev => ({ 
      ...prev, 
      capacity: 1
    }));
    
    // Update the ref to current space
    prevPlaceRef.current = currentPlaceId;
  }, [formData.place?.id, isInitialized, initialBooking]);
  
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsCalendarOpen(false);
        setActiveDatePicker(null);
        setSelectedDateTemp(null);
      }
    };
    if (isCalendarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      updateCalendarPosition();
      window.addEventListener('resize', updateCalendarPosition);
      window.addEventListener('scroll', updateCalendarPosition);
      
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener('resize', updateCalendarPosition);
        window.removeEventListener('scroll', updateCalendarPosition);
      };
    }
  }, [isCalendarOpen, updateCalendarPosition]);
  
  useEffect(() => {
    if (isCalendarOpen) {
      updateCalendarPosition();
    }
  }, [selectedDateTemp, startTime, updateCalendarPosition, isCalendarOpen]);
  
  // Fetch groups
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setLoadingGroups(true);
        const response = await PlaceGroupService.getGroups();
        const formattedGroups = [
          { id: '%7Bid%7D', label: t("modal.newbooking.noGroup") || "No group", value: '%7Bid%7D' },
          ...response.data.map(group => ({
            id: group.id,
            label: group.label,
            value: group.id,
          })),
        ];
        setGroups(formattedGroups);
        console.log('[BookingModal] ✅ Groups loaded:', formattedGroups.length);
      } catch (err) {
        setError(err.message || "Failed to fetch groups");
      } finally {
        setLoadingGroups(false);
      }
    };
    fetchGroups();
  }, [PlaceGroupService, t]);
  
  // Fetch spaces - handles both groupFilter and editing with place
// Fetch spaces - handles both groupFilter and editing with place
useEffect(() => {
  // CRITICAL: Skip fetching during initialization when editing
  if (initialBooking && !isInitialized) {
    console.log('[BookingModal] 🔄 Skipping fetch during initialization');
    return;
  }
  
  const fetchSpaces = async () => {
    // Skip if spaces are already loaded
    if (spacesLoadedRef.current) {
      console.log('[BookingModal] 🔄 Spaces already loaded, skipping fetch');
      return;
    }
    
    try {
      setLoadingSpaces(true);
      let spacesData = [];
      
      // If we're editing and have a place but no groupFilter, fetch all spaces
      if (initialBooking?.place?.id && !formData.groupFilter) {
        console.log('[BookingModal] 🔄 Fetching all spaces for editing (no groupFilter)');
        try {
          const groupsResponse = await PlaceGroupService.getGroups();
          const allSpaces = [];
          
          for (const group of groupsResponse.data) {
            try {
              const response = await PlaceGroupService.getPlaces(group.id);
              allSpaces.push(...response.data);
            } catch (e) {
              console.warn('Error fetching spaces for group:', group.id);
            }
          }
          
          try {
            const noGroupResponse = await PlaceGroupService.getWithoutGroupPlaces();
            allSpaces.push(...noGroupResponse.data);
          } catch (e) {
            console.warn('Error fetching spaces without groups');
          }
          
          spacesData = allSpaces;
          console.log('[BookingModal] ✅ All spaces fetched:', spacesData.length);
        } catch (e) {
          console.error('Error fetching all spaces:', e);
        }
      } 
      // Normal flow - fetch spaces based on group filter
      else if (formData.groupFilter) {
        // Check if it's the "No group" option (id === '%7Bid%7D')
        if (formData.groupFilter.id === '%7Bid%7D') {
          console.log('[BookingModal] 🔄 Fetching spaces without group');
          const response = await PlaceGroupService.getWithoutGroupPlaces();
          spacesData = response.data;
          console.log('[BookingModal] ✅ Spaces without group fetched:', spacesData.length);
        } else {
          console.log('[BookingModal] 🔄 Fetching spaces for group:', formData.groupFilter.id);
          const response = await PlaceGroupService.getPlaces(formData.groupFilter.id);
          spacesData = response.data;
          console.log('[BookingModal] ✅ Spaces for group fetched:', spacesData.length);
        }
      } else {
        // No group filter and no initial booking - clear spaces
        console.log('[BookingModal] 🔄 No group filter, no initial booking - clearing spaces');
        setSpaces([]);
        setLoadingSpaces(false);
        return;
      }
      
      const mappedSpaces = spacesData.map(space => ({
        id: space.id,
        label: space.name,
        value: space.id,
        placeUsage: space.placeUsage,
        minimal_duration: space.minimal_duration,
        name: space.name,
      }));
      
      // If we have a selected space, make sure it's in the list
      if (formData.place) {
        const selectedSpaceExists = mappedSpaces.some(s => s.value === formData.place.value);
        if (!selectedSpaceExists) {
          mappedSpaces.push(formData.place);
          console.log('[BookingModal] 🔄 Added selected space to list:', formData.place.label);
        }
      }
      
      setSpaces(mappedSpaces);
      spacesLoadedRef.current = true;
      console.log('[BookingModal] ✅ Spaces loaded:', mappedSpaces.length);
    } catch (err) {
      console.error("Error fetching spaces:", err);
      setError("Failed to fetch spaces");
    } finally {
      setLoadingSpaces(false);
    }
  };
  
  // Only fetch if we have a groupFilter (including "No group") OR we're editing with a place
  // AND spaces are not already loaded
  if ((formData.groupFilter || (initialBooking?.place?.id && !formData.groupFilter)) && !spacesLoadedRef.current) {
    fetchSpaces();
  } else if (!formData.groupFilter && !initialBooking?.place?.id) {
    setSpaces([]);
    setLoadingSpaces(false);
  }
}, [formData.groupFilter?.id, initialBooking?.place?.id, isInitialized]);
  
  // Fetch staff
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        setLoadingStaff(true);
        const response = await StaffService.getStaff(token);
        setStaff(response.data.map(person => ({
          id: person.id,
          label: person.name,
          value: person.id,
        })));
        console.log('[BookingModal] ✅ Staff loaded:', response.data.length);
      } catch (err) {
        setError(err.message === "Authentication failed" ? "Authentication failed" : "Failed to fetch staff");
      } finally {
        setLoadingStaff(false);
      }
    };
    fetchStaff();
  }, [token]);
  
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "number" ? parseInt(value) || 0 : value,
    }));
  };
  
  const handleDropdownChange = (field) => (value) => {
    console.log('[BookingModal] 🔄 handleDropdownChange - field:', field, 'value:', value);
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      if (field === "groupFilter") {
        // Only reset spaces if the group actually changed
        if (prev.groupFilter?.id !== value?.id) {
          spacesLoadedRef.current = false;
          newData.place = null;
          setSelectedSpaceDetails(null);
        }
      }
      return newData;
    });
  };
  
  const handleCapacityChange = (newCapacity) => {
    if (newCapacity < 1) return;
    if (maxCapacity && newCapacity > maxCapacity) return;
    
    setCapacity(newCapacity);
    setFormData(prev => ({ ...prev, capacity: newCapacity }));
    setFieldErrors(prev => ({ ...prev, capacity: null }));
  };
  
  const openDatePicker = (type) => {
    setActiveDatePicker(type);
    setSelectedDateTemp(type === 'start' ? startDate : endDate);
    setIsCalendarOpen(true);
    if (type === 'start' && startDate) {
      setCurrentMonth(startDate);
    } else if (type === 'end' && endDate) {
      setCurrentMonth(endDate);
    } else {
      setCurrentMonth(new Date());
    }
  };
  
  const handleDateSelect = (date) => {
    const stableDate = createStableLocalDate(date);
    if (!stableDate) return;
    
    setSelectedDateTemp(stableDate);
    
    if (activeDatePicker === 'start') {
      setStartTime(null);
    } else if (activeDatePicker === 'end') {
      setEndTime(null);
    }
  };
  
  const handleConfirmTime = () => {
    if (activeDatePicker === 'start' && selectedDateTemp && startTime) {
      setStartDate(selectedDateTemp);
      if (endDate && isBefore(endDate, selectedDateTemp)) {
        setEndDate(null);
        setEndTime(null);
      }
      setIsCalendarOpen(false);
      setActiveDatePicker(null);
      setSelectedDateTemp(null);
      setError(null);
    } else if (activeDatePicker === 'end' && selectedDateTemp && endTime) {
      if (startDate && isBefore(selectedDateTemp, startDate)) {
        setError(t("modal.newbooking.endDateBeforeStart") || "End date cannot be before start date");
        return;
      }
      setEndDate(selectedDateTemp);
      setIsCalendarOpen(false);
      setActiveDatePicker(null);
      setSelectedDateTemp(null);
      setError(null);
    }
  };
  
  const handleStartTimeSelect = (time) => {
    setStartTime(time);
  };
  
  const handleEndTimeSelect = (time) => {
    setEndTime(time);
  };
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    let firstDayOfWeek = firstDay.getDay();
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };
  
  const getDateStatus = useCallback((date, isStartPicker = true) => {
    if (!date) return 'empty';
    
    const stableDate = createStableLocalDate(date);
    if (!stableDate) return 'empty';
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (stableDate < today) return 'past';
    
    if (!isStartPicker && startDate && isBefore(stableDate, startDate)) {
      return 'disabled';
    }
    
    const intervals = getWorkingIntervalsForDate(stableDate);
    const isWorking = intervals.length > 0;
    if (!isWorking) return 'unavailable';
    
    const isToday = stableDate.getTime() === today.getTime();
    if (isToday) return 'today';
    
    return 'available';
  }, [getWorkingIntervalsForDate, startDate]);
  
  const daysInMonth = getDaysInMonth(currentMonth);
  
  const validateForm = () => {
    const errors = [];
    
    if (!startDate) {
      errors.push(t("modal.newbooking.startDateRequired") || "Start date is required");
    }
    if (!endDate) {
      errors.push(t("modal.newbooking.endDateRequired") || "End date is required");
    }
    if (!startTime) {
      errors.push(t("modal.newbooking.startTimeRequired") || "Start time is required");
    }
    if (!endTime) {
      errors.push(t("modal.newbooking.endTimeRequired") || "End time is required");
    }
    if (!formData.title) {
      errors.push(t("modal.newbooking.titleRequired") || "Title is required");
    }
    if (!formData.place) {
      errors.push(t("modal.newbooking.spaceRequired") || "Space is required");
    }
    if (!formData.customer_first_name || !formData.customer_last_name || !formData.customer_email) {
      errors.push(t("modal.newbooking.customerInfoRequired") || "Customer information is required");
    }
    
    return errors;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(", "));
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const bookingData = {
        id: initialBooking?.id,
        title: formData.title,
        place_id: formData.place?.id,
        staff_id: formData.staff?.id,
        customer_social_reason: formData.customer_social_reason,
        customer_first_name: formData.customer_first_name,
        customer_last_name: formData.customer_last_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        services_equipments: formData.services_equipments,
        capacity: isCoworking ? 0 : capacity,
        coworking_stations: isCoworking ? capacity : 0,
        disposition_type: formData.disposition_type?.keyByName || formData.disposition_type || "",
        start_date: formatDateToString(startDate),
        start_hour: startTime,
        end_date: formatDateToString(endDate),
        end_hour: endTime,
      };
      
      const result = await onSubmit(bookingData, sendMail);
      if (result.success) {
        setNewbookingSuccess(true);
      } else {
        setError(result.error || t("modal.newbooking.unknownError") || "Unknown error occurred");
      }
    } catch (error) {
      console.error("Submission error:", error);
      setError(error.message || t("modal.newbooking.unknownError") || "Unknown error occurred");
    } finally {
      setIsLoading(false);
    }
  };
  
  if (newbookingSuccess) {
    return <SuccessState onClose={onClose} t={t} />;
  }
  
  const showExpandedCalendar = (activeDatePicker === 'start' && selectedDateTemp) || (activeDatePicker === 'end' && selectedDateTemp && startTime);
  
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer} ref={modalContainerRef}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.modalBody}>
            {/* Booking Title - First */}
            <FormSection>
              <TextInput
                label={t("modal.newbooking.titleLabel")}
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                disabled={isLoading}
              />
              
              <FormRow>
                <SelectInput
                  label={t("modal.newbooking.groupLabel")}
                  options={groups}
                  value={formData.groupFilter ? formData.groupFilter.value : null}
                  onChange={(value) => {
                    console.log('[BookingModal] 🔄 Group selected - value:', value);
                    const selectedGroup = groups.find(g => g.value === value) || null;
                    console.log('[BookingModal] 🔄 Selected group object:', selectedGroup);
                    handleDropdownChange("groupFilter")(selectedGroup);
                  }}
                  disabled={isLoading || loadingGroups}
                  isLoading={loadingGroups}
                />
                
                <SelectInput
                  label={t("modal.newbooking.spaceLabel")}
                  options={spaces}
                  value={formData.place ? formData.place.value : null}
                  onChange={(value) => {
                    console.log('[BookingModal] 🔄 Space selected - value:', value);
                    console.log('[BookingModal] 🔄 Available spaces:', spaces);
                    const selectedSpace = spaces.find(s => s.value === value) || null;
                    console.log('[BookingModal] 🔄 Selected space object:', selectedSpace);
                    if (selectedSpace) {
                      handleDropdownChange("place")(selectedSpace);
                    }
                  }}
                  required
                  disabled={isLoading || loadingSpaces}
                  isLoading={loadingSpaces}
                />
                
                <SelectInput
                  label={t("modal.newbooking.userLabel")}
                  options={staff}
                  value={formData.staff ? formData.staff.value : null}
                  onChange={(value) => {
                    console.log('[BookingModal] 🔄 Staff selected - value:', value);
                    const selectedStaff = staff.find(s => s.value === value) || null;
                    console.log('[BookingModal] 🔄 Selected staff object:', selectedStaff);
                    handleDropdownChange("staff")(selectedStaff);
                  }}
                  disabled={isLoading || loadingStaff}
                  isLoading={loadingStaff}
                />
              </FormRow>
            </FormSection>

            {/* Customer Information Section */}
            <FormSection title={t("modal.newbooking.customerSection")}>
              <TextInput
                label={t("modal.newbooking.tradenameLabel")}
                name="customer_social_reason"
                value={formData.customer_social_reason}
                onChange={handleChange}
                disabled={isLoading}
              />
              
              <FormRow>
                <TextInput
                  label={t("modal.newbooking.firstnameLabel")}
                  name="customer_first_name"
                  value={formData.customer_first_name}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                />
                <TextInput
                  label={t("modal.newbooking.lastnameLabel")}
                  name="customer_last_name"
                  value={formData.customer_last_name}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                />
              </FormRow>
              
              <FormRow>
                <TextInput
                  label={t("modal.newbooking.emailLabel")}
                  name="customer_email"
                  type="email"
                  value={formData.customer_email}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                />
                <TextInput
                  label={t("modal.newbooking.phoneLabel")}
                  name="customer_phone"
                  value={formData.customer_phone}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </FormRow>
            </FormSection>

            {/* Booking Section - Date and Time */}
            <FormSection title={t("modal.newbooking.bookingSection")}>
              <FormRow>
                <div className={styles.datePickerWrapper}>
                  <label className={styles.dateLabel}>{t("modal.newbooking.fromLabel")}</label>
                  <button 
                    ref={startDateButtonRef}
                    className={styles.dateButton} 
                    onClick={() => openDatePicker('start')} 
                    type="button" 
                    disabled={isLoading}
                  >
                    {startDate ? (
                      <SelectedDateDisplay 
                        selectedDate={startDate} 
                        selectedTime={startTime}
                        currentLanguage={currentLanguage}
                      />
                    ) : (
                      <span className={styles.placeholderDate}>{t("modal.newbooking.selectStartDate") || "Select start date & time"}</span>
                    )}
                    <svg className={styles.calendarIcon} width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M16 2V6" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M8 2V6" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M3 10H21" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </button>
                </div>
                
                <div className={styles.datePickerWrapper}>
                  <label className={styles.dateLabel}>{t("modal.newbooking.toLabel")}</label>
                  <button 
                    ref={endDateButtonRef}
                    className={styles.dateButton} 
                    onClick={() => openDatePicker('end')} 
                    type="button" 
                    disabled={isLoading || !startDate}
                  >
                    {endDate ? (
                      <SelectedDateDisplay 
                        selectedDate={endDate} 
                        selectedTime={endTime}
                        currentLanguage={currentLanguage}
                      />
                    ) : (
                      <span className={styles.placeholderDate}>
                        {startDate ? (t("modal.newbooking.selectEndDate") || "Select end date & time") : (t("modal.newbooking.selectStartFirst") || "Select start date first")}
                      </span>
                    )}
                    <svg className={styles.calendarIcon} width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M16 2V6" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M8 2V6" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M3 10H21" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </button>
                </div>
              </FormRow>
              
              {isCalendarOpen && (
                <div 
                  className={`${styles.calendarPopup} ${showExpandedCalendar ? styles.expanded : ''}`} 
                  ref={calendarRef}
                  style={{
                    position: 'absolute',
                    top: `${calendarPosition.top}px`,
                    left: `${calendarPosition.left}px`,
                    zIndex: 1001
                  }}
                >
                  <div className={styles.popupContent}>
                    <div className={styles.calendarSection}>
                      <div className={styles.calendarHeader}>
                        <button type="button" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}>‹</button>
                        <span>{currentMonth.toLocaleString(currentLanguage, { month: "long" })} {currentMonth.getFullYear()}</span>
                        <button type="button" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}>›</button>
                      </div>
                      
                      <div className={styles.weekdays}>
                        {weekdays.map(day => (
                          <span key={day}>{day}</span>
                        ))}
                      </div>
                      
                      <div className={styles.days}>
                        {loadingAvailability && (
                          <div className={styles.loadingOverlay}>
                            <div className={styles.smallSpinner}></div>
                          </div>
                        )}
                        {daysInMonth.map((date, idx) => {
                          if (!date) return <span key={`empty-${idx}`} className={styles.emptyDay} />;
                          
                          const isStartPicker = activeDatePicker === 'start';
                          const status = getDateStatus(date, isStartPicker);
                          const isSelected = selectedDateTemp && isSameDate(date, selectedDateTemp);
                          
                          let dateClass = styles.day;
                          if (status === 'past' || status === 'unavailable' || status === 'disabled') dateClass += ` ${styles.unavailable}`;
                          if (status === 'today') dateClass += ` ${styles.today}`;
                          if (isSelected) dateClass += ` ${styles.selected}`;
                          
                          const isDisabled = status === 'past' || status === 'unavailable' || status === 'disabled';
                          
                          return (
                            <button
                              key={date.toISOString()}
                              className={dateClass}
                              onClick={() => (status === 'available' || status === 'today') && handleDateSelect(date)}
                              disabled={isDisabled || isLoading}
                              type="button"
                            >
                              {date.getDate()}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Start Time Selection - Right Side */}
                    {activeDatePicker === 'start' && selectedDateTemp && (
                      <div className={`${styles.selectionSection} ${styles.selectionSectionRight}`}>
                        <div className={styles.timeField}>
                          <label>{t("modal.newbooking.startTime") || "Select Start Time"}</label>
                          {loadingAvailability ? (
                            <p className={styles.loadingMessage}>{t("modal.newbooking.loadingTimeSlots") || "Loading time slots..."}</p>
                          ) : availableStartTimes.length > 0 ? (
                            <div className={styles.timeOptions}>
                              {availableStartTimes.map(slot => (
                                <button
                                  key={slot}
                                  type="button"
                                  className={`${styles.timeChip} ${startTime === slot ? styles.selected : ""}`}
                                  onClick={() => handleStartTimeSelect(slot)}
                                >
                                  {slot}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <p className={styles.noSlotsMessage}>{t("modal.newbooking.noTimeSlots") || "No available start times"}</p>
                          )}
                        </div>
                        
                        {startTime && (
                          <button 
                            type="button"
                            className={styles.confirmButton} 
                            onClick={handleConfirmTime}
                          >
                            {t("modal.newbooking.confirmSelection") || "Confirm Start Time"}
                          </button>
                        )}
                      </div>
                    )}
                    
                    {/* End Time Selection - Left Side */}
                    {activeDatePicker === 'end' && selectedDateTemp && startTime && (
                      <div className={`${styles.selectionSection} ${styles.selectionSectionLeft}`}>
                        <div className={styles.timeField}>
                          <label>{t("modal.newbooking.endTime") || "Select End Time"}</label>
                          {loadingAvailability ? (
                            <p className={styles.loadingMessage}>{t("modal.newbooking.loadingTimeSlots") || "Loading time slots..."}</p>
                          ) : availableEndTimes.length > 0 ? (
                            <div className={styles.timeOptions}>
                              {availableEndTimes.map(slot => (
                                <button
                                  key={slot}
                                  type="button"
                                  className={`${styles.timeChip} ${endTime === slot ? styles.selected : ""}`}
                                  onClick={() => handleEndTimeSelect(slot)}
                                >
                                  {slot}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <p className={styles.noSlotsMessage}>{t("modal.newbooking.noTimeSlots") || "No available end times"}</p>
                          )}
                        </div>
                        
                        {endTime && (
                          <button 
                            type="button"
                            className={styles.confirmButton} 
                            onClick={handleConfirmTime}
                          >
                            {t("modal.newbooking.confirmSelection") || "Confirm End Time"}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Number of People - with max capacity enforcement */}
              <div className={styles.field}>
                <div className={styles.capacitySelector}>
                  <span className={styles.capacityValue}>
                    {capacity} {capacity === 1 ? t("modal.newbooking.person") || "person" : t("modal.newbooking.people") || "people"}
                    {maxCapacity && ` / ${maxCapacity} max`}
                  </span>
                  <div className={styles.capacityButtons}>
                    <button 
                      type="button"
                      className={styles.capacityBtn} 
                      onClick={() => handleCapacityChange(capacity - 1)} 
                      disabled={capacity <= 1 || isLoading}
                    >
                      -
                    </button>
                    <button 
                      type="button"
                      className={styles.capacityBtn} 
                      onClick={() => handleCapacityChange(capacity + 1)} 
                      disabled={!maxCapacity || capacity >= maxCapacity || isLoading}
                    >
                      +
                    </button>
                  </div>
                </div>
                {fieldErrors.capacity && <span className={styles.errorMessage}>{fieldErrors.capacity}</span>}
              </div>
              
              {/* Disposition Types for Meeting Rooms */}
              {formData.place?.placeUsage?.key === "meeting_room" && (
                <DispositionTypes
                  space={selectedSpaceDetails || formData.place}
                  capacity={capacity}
                  formData={formData}
                  setFormData={setFormData}
                  dispositionTypes={getFilteredDispositionTypes(
                    selectedSpaceDetails || formData.place,
                    t,
                    capacity
                  )}
                />
              )}
            </FormSection>

            {/* Additional Details Section */}
            <FormSection title={t("modal.newbooking.bookingDetailSection")}>
              <TextInput
                name="services_equipments"
                type="textarea"
                value={formData.services_equipments}
                onChange={handleChange}
                disabled={isLoading}
                rows={4}
                placeholder={t("modal.newbooking.additionalDetailsPlaceholder") || "Any special requirements or additional information..."}
              />
            </FormSection>
          </div>
          
          <div className={styles.modalFooter}>
            {error && <ErrorMessage message={error} />}
            
            <FormButtons
              isEditing={!!initialBooking?.id}
              isLoading={isLoading}
              onClose={onClose}
              onDelete={onDelete}
              onSubmit={handleSubmit}
              setSendMail={setSendMail}
              t={t}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingModal;